/**
 * BarcodeScannerV2 — Scanner input for conferência verificação.
 * Supports both HID keyboard wedge and USB-COM serial (Web Serial API).
 */
import React, { useState, useRef, useEffect, useCallback, forwardRef } from 'react';
import { Scan, Volume2, Usb } from 'lucide-react';
import { useSerialScanner } from '../../hooks/useSerialScanner';

interface Props {
  onScan: (barcode: string) => void;
  onInitSound: () => void;
  disabled?: boolean;
}

export const BarcodeScannerV2 = forwardRef<HTMLInputElement, Props>(
  ({ onScan, onInitSound, disabled }, ref) => {
    const [value, setValue] = useState('');
    const [status, setStatus] = useState<'ready' | 'processing'>('ready');
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    // Serial scanner integration
    const handleSerialBarcode = useCallback(async (barcode: string) => {
      if (disabled || !barcode.trim()) return;
      setStatus('processing');
      onInitSound();
      try {
        await onScan(barcode.trim());
      } finally {
        setStatus('ready');
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }, [disabled, onScan, onInitSound, inputRef]);

    const { serialState, isSupported, connect, disconnect } = useSerialScanner(handleSerialBarcode);

    // Auto-focus on mount
    useEffect(() => {
      if (!disabled) {
        setTimeout(() => inputRef.current?.focus(), 200);
      }
    }, [disabled, inputRef]);

    // Keep focus: on page visibility, window focus, and clicks on non-interactive areas
    useEffect(() => {
      if (disabled) return;

      const refocusInput = () => {
        setTimeout(() => {
          if (inputRef.current && document.activeElement !== inputRef.current) {
            // Don't steal focus from modals (z-50+ elements)
            const active = document.activeElement as HTMLElement | null;
            if (active) {
              const zIndex = parseInt(window.getComputedStyle(active).zIndex || '0', 10);
              if (zIndex >= 50) return;
              // Don't steal from other inputs/selects inside modals
              const inModal = active.closest('[role="dialog"], .fixed');
              if (inModal) return;
            }
            inputRef.current.focus();
          }
        }, 150);
      };

      const handleVisibility = () => {
        if (document.visibilityState === 'visible') refocusInput();
      };

      const handleWindowFocus = () => refocusInput();

      const handleDocClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        // Only refocus if click was on non-interactive element (page background, table cells, etc.)
        const interactive = target.closest('input, button, select, textarea, a, [role="button"], [tabindex]');
        if (!interactive) {
          refocusInput();
        }
      };

      document.addEventListener('visibilitychange', handleVisibility);
      window.addEventListener('focus', handleWindowFocus);
      document.addEventListener('click', handleDocClick);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('focus', handleWindowFocus);
        document.removeEventListener('click', handleDocClick);
      };
    }, [disabled, inputRef]);

    const handleSubmit = useCallback(async () => {
      const trimmed = value.trim();
      if (!trimmed || disabled) return;

      setStatus('processing');
      onInitSound();
      setValue('');

      try {
        await onScan(trimmed);
      } finally {
        setStatus('ready');
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }, [value, disabled, onScan, onInitSound, inputRef]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    }, [handleSubmit]);

    const handleSerialToggle = useCallback(async () => {
      onInitSound();
      if (serialState === 'connected') {
        await disconnect();
      } else {
        await connect();
      }
    }, [serialState, connect, disconnect, onInitSound]);

    const isSerialConnected = serialState === 'connected';

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-blue-300 dark:border-blue-700 p-3 sticky top-0 z-10 shadow-md">
        <div className="flex items-center gap-3">
          <Scan className={`w-5 h-5 flex-shrink-0 ${
            status === 'processing' ? 'text-amber-500 animate-pulse' : 'text-blue-500'
          }`} />

          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={disabled ? 'Conferência finalizada' : 'Código de Barras (auto-focus)...'}
            className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono disabled:opacity-50"
            autoComplete="off"
            autoFocus={!disabled}
          />

          <button
            onClick={handleSubmit}
            disabled={!value.trim() || disabled || status === 'processing'}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {status === 'processing' ? 'Processando...' : 'Enviar'}
          </button>

          <button
            onClick={onInitSound}
            className="p-2 text-gray-400 hover:text-blue-500 rounded-lg"
            title="Ativar sons"
          >
            <Volume2 className="w-4 h-4" />
          </button>

          {isSupported && !disabled && (
            <button
              onClick={handleSerialToggle}
              disabled={serialState === 'connecting'}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                isSerialConnected
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : serialState === 'connecting'
                    ? 'bg-amber-500 text-white cursor-wait'
                    : serialState === 'error'
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              title={isSerialConnected ? 'Clique para desconectar' : 'Conectar scanner USB-COM via porta serial'}
            >
              <Usb className="w-3.5 h-3.5" />
              {isSerialConnected
                ? 'Scanner Conectado'
                : serialState === 'connecting'
                  ? 'Conectando...'
                  : serialState === 'error'
                    ? 'Reconectar Scanner'
                    : 'Conectar Scanner'}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
          <span className={`w-2 h-2 rounded-full ${
            status === 'processing' ? 'bg-amber-400' : 'bg-green-400'
          }`} />
          <span>
            {status === 'processing' ? 'Processando leitura...' : 'Pronto — Escaneie ou digite o código'}
          </span>
          {isSupported && (
            <span className={`ml-auto flex items-center gap-1 ${
              isSerialConnected ? 'text-green-500' : 'text-gray-400'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                isSerialConnected ? 'bg-green-400' : 'bg-gray-400'
              }`} />
              Serial: {isSerialConnected ? 'Conectado' : 'Desconectado'}
            </span>
          )}
        </div>
      </div>
    );
  }
);

BarcodeScannerV2.displayName = 'BarcodeScannerV2';
